import React, { useState } from "react";

import { useFetchBiSalesQuery, useFetchBiTransactionsQuery } from "../../../api/analyticsApi";
import { Chip, Segmented } from "../../catalogProductStudio/edit/StudioUi";
import { SimpleBarChart, TrendChart } from "../AnalyticsCharts";
import { BarList, DataNotes, DataState, DataTable, ExportButton, KpiCard, Pager, Panel } from "../AnalyticsUi";
import { formatDate, formatDateTime, formatMoney, formatNumber, formatPercent, weekdayLabel } from "../analyticsModel";

const SUMMARY = [
  { key: "grossSales", money: true },
  { key: "discounts", money: true, invert: true },
  { key: "refunds", money: true, invert: true },
  { key: "netRevenue", money: true, label: "kpi_netRevenue" },
  { key: "averageOrderValue", money: true, label: "kpi_averageOrderValue" },
  { key: "unitsPerTransaction", digits: 2 },
  { key: "discountRate", percent: true, invert: true },
  { key: "refundRate", percent: true, invert: true },
];

const ChannelCompare = ({ channels, tt, lang }) => {
  const rows = [
    { key: "revenue", money: true },
    { key: "refunds", money: true },
    { key: "netRevenue", money: true, label: "kpi_netRevenue" },
    { key: "salesCount", label: "kpi_salesCount" },
    { key: "averageOrderValue", money: true, label: "kpi_averageOrderValue" },
    { key: "units", label: "kpi_unitsSold" },
    { key: "unitsPerTransaction", digits: 2 },
    { key: "discounts", money: true },
  ];
  const format = (row, value) => (row.money ? formatMoney(value, lang) : formatNumber(value, lang, row.digits || 0));
  return (
    <div className="an-table-wrap">
      <table className="an-table">
        <thead>
          <tr>
            <th scope="col" />
            <th scope="col" className="is-end">{tt("channel_store")}</th>
            <th scope="col" className="is-end">{tt("channel_online")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <th scope="row">{tt(row.label || row.key)}</th>
              <td className="is-end">{format(row, channels.store[row.key])}</td>
              <td className="is-end">{format(row, channels.online[row.key])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const OrdersPanel = ({ orders, tt, lang }) => {
  const statuses = ["PENDING", "CONFIRMED", "DELIVERED", "CANCELLED", "RETURNED_TO_SENDER"];
  return (
    <Panel id="an-orders" title={tt("ordersTitle")} hint={tt("ordersHint")}>
      <dl className="an-facts">
        <div>
          <dt>{tt("ordersCreated")}</dt>
          <dd>{formatNumber(orders.created, lang)}</dd>
        </div>
        {statuses.map((status) => (
          <div key={status}>
            <dt>{tt(`status_${status}`)}</dt>
            <dd>{formatNumber(orders.byStatus[status]?.count ?? 0, lang)}</dd>
          </div>
        ))}
        <div>
          <dt>{tt("cancellationRate")}</dt>
          <dd>{formatPercent(orders.cancellationRate, lang)}</dd>
        </div>
        <div>
          <dt>{tt("deliveryFees")}</dt>
          <dd>{formatMoney(orders.deliveryFees, lang)}</dd>
        </div>
      </dl>
      {Object.keys(orders.cancellationReasons).length ? (
        <div className="an-chips">
          {Object.entries(orders.cancellationReasons).map(([reason, count]) => (
            <Chip key={reason}>
              {tt(`reason_${reason}`)} · {count}
            </Chip>
          ))}
        </div>
      ) : null}
      {orders.openPipeline.length ? (
        <>
          <h4 className="an-subhead">{tt("openPipeline")}</h4>
          <div className="an-chips">
            {orders.openPipeline.map((row) => (
              <Chip key={row.status} tone="info">
                {tt(`status_${row.status}`)} · {row.count} · {formatMoney(row.merchandise, lang)}
              </Chip>
            ))}
          </div>
        </>
      ) : null}
    </Panel>
  );
};

const TransactionsPanel = ({ range, tt, lang }) => {
  const [page, setPage] = useState(1);
  const [channel, setChannel] = useState("");
  const params = { dateFrom: range.dateFrom, dateTo: range.dateTo, channel: channel || undefined };
  const query = useFetchBiTransactionsQuery({ ...params, page, pageSize: 20 });

  return (
    <Panel
      id="an-transactions"
      title={tt("transactions")}
      actions={
        <>
          <Segmented
            ariaLabel={tt("col_channel")}
            value={channel}
            onChange={(value) => {
              setChannel(value);
              setPage(1);
            }}
            options={[
              { value: "", label: tt("all") },
              { value: "store", label: tt("channel_STORE") },
              { value: "online", label: "Online" },
            ]}
          />
          <ExportButton dataset="transactions" params={params} tt={tt} />
        </>
      }
    >
      <DataState query={query} tt={tt}>
        {query.data ? (
          <>
            <DataTable
              caption={tt("transactions")}
              rows={query.data.items}
              rowKey={(row) => row.id}
              emptyText={tt("emptyTitle")}
              columns={[
                {
                  key: "sale",
                  label: tt("col_sale"),
                  render: (row) => (
                    <span className="an-cell-stack">
                      <strong>{row.saleNumber}</strong>
                      {row.isExchange ? <span className="an-tag">{tt("exchangeTag")}</span> : null}
                    </span>
                  ),
                },
                { key: "date", label: tt("col_date"), render: (row) => formatDateTime(row.completedAt, lang) },
                {
                  key: "channel",
                  label: tt("col_channel"),
                  render: (row) =>
                    row.channel === "store" ? tt("channel_STORE") : `${tt(`channel_${row.orderChannel || "UNSPECIFIED"}`)} · ${row.orderNumber || ""}`,
                },
                { key: "payment", label: tt("col_payment"), render: (row) => (row.paymentMethod ? tt(`method_${row.paymentMethod}`) : "—") },
                { key: "units", label: tt("col_units"), align: "end", render: (row) => formatNumber(row.units, lang) },
                { key: "discount", label: tt("col_discount"), align: "end", render: (row) => (row.discounts ? formatMoney(row.discounts, lang) : "—") },
                { key: "total", label: tt("col_total"), align: "end", render: (row) => <strong>{formatMoney(row.total, lang)}</strong> },
                { key: "refunded", label: tt("col_refunded"), align: "end", render: (row) => (row.refunded ? formatMoney(row.refunded, lang) : "—") },
              ]}
            />
            <Pager pagination={query.data.pagination} onPage={setPage} tt={tt} />
          </>
        ) : null}
      </DataState>
    </Panel>
  );
};

const SalesSection = ({ range, tt, lang }) => {
  const query = useFetchBiSalesQuery({ dateFrom: range.dateFrom, dateTo: range.dateTo, granularity: range.granularity });
  const [metric, setMetric] = useState("revenue");
  const data = query.data;

  return (
    <DataState query={query} tt={tt}>
      {data ? (
        <>
          <div className="an-kpis an-kpis--compact">
            {SUMMARY.map((item) => {
              const compared = data.summary[item.key];
              const display = item.percent
                ? formatPercent(compared.value, lang)
                : item.money
                ? formatMoney(compared.value, lang)
                : formatNumber(compared.value, lang, item.digits || 0);
              return <KpiCard key={item.key} tt={tt} lang={lang} label={tt(item.label || item.key)} compared={compared} display={display} invert={item.invert} />;
            })}
          </div>
          <DataNotes notes={data.notes} tt={tt} lang={lang} />

          <Panel
            id="an-sales-trend"
            title={tt("salesTrend")}
            hint={tt("revenueTrendHint")}
            actions={
              <Segmented
                ariaLabel={tt("salesTrend")}
                value={metric}
                onChange={setMetric}
                options={[
                  { value: "revenue", label: tt("revenue") },
                  { value: "discounts", label: tt("discounts") },
                  { value: "refunds", label: tt("refunds") },
                  { value: "salesCount", label: tt("sales") },
                  { value: "units", label: tt("units") },
                ]}
              />
            }
          >
            <TrendChart
              points={data.trend}
              field={metric}
              granularity={data.granularity}
              label={tt(metric === "salesCount" ? "sales" : metric)}
              money={!["salesCount", "units"].includes(metric)}
              lang={lang}
              tt={tt}
            />
          </Panel>

          <div className="an-grid an-grid--2">
            <Panel id="an-store-online" title={tt("storeVsOnline")}>
              <ChannelCompare channels={data.channels} tt={tt} lang={lang} />
              {data.onlineChannels.length ? (
                <>
                  <h4 className="an-subhead">{tt("onlineBreakdown")}</h4>
                  <BarList
                    tone="2"
                    rows={data.onlineChannels.map((row) => ({ ...row, key: row.channel }))}
                    valueKey="revenue"
                    labelOf={(row) => tt(`channel_${row.channel}`)}
                    format={(value, row) => `${formatMoney(value, lang)} · ${formatNumber(row.salesCount, lang)}`}
                  />
                </>
              ) : null}
            </Panel>
            <OrdersPanel orders={data.orders} tt={tt} lang={lang} />
          </div>

          <div className="an-grid an-grid--2">
            <Panel id="an-weekday" title={tt("weekdayTitle")} hint={tt("weekdayHint")}>
              <SimpleBarChart
                labels={data.weekdays.map((row) => weekdayLabel(row.weekday, lang))}
                values={data.weekdays.map((row) => row.averageRevenue ?? 0)}
                label={tt("weekdayTitle")}
                lang={lang}
                tt={tt}
              />
            </Panel>
            <Panel id="an-hour" title={tt("hourTitle")} hint={tt("hourHint")}>
              <SimpleBarChart
                labels={data.hours.map((row) => String(row.hour).padStart(2, "0"))}
                values={data.hours.map((row) => row.revenue)}
                label={tt("hourTitle")}
                lang={lang}
                tt={tt}
              />
            </Panel>
          </div>

          <div className="an-grid an-grid--2">
            <Panel id="an-peak" title={tt("peakDays")}>
              <BarList
                rows={data.peakDays.map((row) => ({ ...row, key: row.date }))}
                valueKey="revenue"
                labelOf={(row) => formatDate(row.date, lang, { weekday: "short", day: "2-digit", month: "short", year: "numeric" })}
                format={(value, row) => `${formatMoney(value, lang)} · ${formatNumber(row.salesCount, lang)}`}
                emptyText={tt("emptyTitle")}
              />
            </Panel>
            <Panel id="an-payment" title={tt("paymentMethods")}>
              <BarList
                rows={data.paymentMethods.map((row) => ({ ...row, key: row.method }))}
                valueKey="revenue"
                labelOf={(row) => tt(`method_${row.method}`)}
                format={(value, row) => `${formatMoney(value, lang)} · ${formatNumber(row.salesCount, lang)}`}
                emptyText={tt("emptyTitle")}
              />
            </Panel>
          </div>

          <TransactionsPanel range={range} tt={tt} lang={lang} />
        </>
      ) : null}
    </DataState>
  );
};

export default SalesSection;
